import { PaginationControls } from "@/components/pagination-controls";
import {
  DEFAULT_RECURRENCE_RULE,
  RecurrenceRulePicker,
  RecurrenceRuleValue,
  validateRecurrenceRule,
} from "@/components/RecurrenceRulePicker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { usePagination } from "@/hooks/use-pagination";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import {
  notificationsService,
  type CreateNotificationPayload,
} from "@/lib/api/notifications.service";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { queryClient } from "@/lib/queryClient";
import type {
  Notification,
  NotificationCategory,
  NotificationScope,
  NotificationStatus,
  NotificationTargetFilter,
} from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertCircle,
  Bell,
  Clock,
  FileText,
  Filter,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

const categoryLabels: Record<NotificationCategory, string> = {
  system: "System",
  account_billing: "Account & Billing",
  content: "Content",
};

const statusColors: Record<NotificationStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  sent: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  cancelled: "bg-muted text-muted-foreground",
};

const scopeIcons: Record<NotificationScope, typeof Users> = {
  all: Users,
  segment: Users,
  individual: User,
};

export default function Notifications() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const canViewNotifications =
    user?.roles &&
    (hasPermission(user.roles, "notifications", "view_user_notifications") ||
      hasPermission(user.roles, "notifications", "view_coach_notifications"));
  const canCreate =
    user?.roles &&
    (hasPermission(user.roles, "notifications", "send_to_all") ||
      hasPermission(user.roles, "notifications", "send_to_segment") ||
      hasPermission(user.roles, "notifications", "send_to_individual"));
  const canSchedule =
    user?.roles &&
    hasPermission(user.roles, "notifications", "schedule_notification");
  const canCancel =
    user?.roles &&
    hasPermission(user.roles, "notifications", "cancel_notification");
  const canSendToAll =
    user?.roles && hasPermission(user.roles, "notifications", "send_to_all");
  const canSendToSegment =
    user?.roles &&
    hasPermission(user.roles, "notifications", "send_to_segment");
  const canSendToIndividual =
    user?.roles &&
    hasPermission(user.roles, "notifications", "send_to_individual");

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data: notifResponse, isLoading } = useQuery({
    queryKey: ["/api/notifications", statusFilter, debouncedSearch],
    queryFn: () =>
      notificationsService.list({
        status: statusFilter === "all" ? undefined : statusFilter,
        search: debouncedSearch || undefined,
        pageSize: 50,
      }),
    enabled: !!canViewNotifications,
  });
  const notifications = notifResponse?.items ?? [];
  const counts = notifResponse?.counts;
  // Tab counts use the server's per-status counts, which are computed over the
  // full (unfiltered) set — independent of the active statusFilter. "All" is
  // their sum; do NOT use notifResponse.total, which the server scopes to the
  // active status filter (that's what made "All" collapse to the filtered count).
  const totalCount = counts
    ? counts.draft +
      counts.scheduled +
      counts.sent +
      counts.failed +
      counts.cancelled
    : 0;

  const createMutation = useMutation({
    mutationFn: (data: CreateNotificationPayload) =>
      notificationsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      setIsCreateOpen(false);
      toast({ title: "Notification created" });
    },
    onError: () => {
      toast({ title: "Failed to create notification", variant: "destructive" });
    },
  });

  const sendMutation = useMutation({
    mutationFn: (id: string) => notificationsService.send(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "Notification sent successfully" });
    },
    onError: () => {
      toast({ title: "Failed to send notification", variant: "destructive" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => notificationsService.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "Notification cancelled" });
    },
    onError: () => {
      toast({ title: "Failed to cancel notification", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => notificationsService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "Notification deleted from list" });
    },
    onError: () => {
      toast({ title: "Failed to delete notification", variant: "destructive" });
    },
  });

  if (!canViewNotifications) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-medium">Access Restricted</h3>
          <p className="text-muted-foreground mt-1">
            Notification management requires elevated permissions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            Notifications
          </h1>
          <p className="text-muted-foreground mt-1">
            Communicate with users through system messages
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setIsCreateOpen(true)}
            data-testid="button-create-notification"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Notification
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="input-search-notifications"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className="w-[150px]"
            data-testid="select-status-filter"
          >
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <Tabs
          value={statusFilter === "all" ? "all" : statusFilter}
          onValueChange={(v) => setStatusFilter(v)}
          className="space-y-4"
        >
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">
              All ({totalCount})
            </TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-drafts">
              Drafts ({counts?.draft ?? 0})
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled">
              Scheduled ({counts?.scheduled ?? 0})
            </TabsTrigger>
            <TabsTrigger value="sent" data-testid="tab-sent">
              Sent ({counts?.sent ?? 0})
            </TabsTrigger>
            <TabsTrigger value="failed" data-testid="tab-failed">
              Failed ({counts?.failed ?? 0})
            </TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled">
              Cancelled ({counts?.cancelled ?? 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value={statusFilter === "all" ? "all" : statusFilter}
            className="space-y-4"
          >
            <NotificationList
              notifications={notifications}
              onSend={(id) => sendMutation.mutate(id)}
              onCancel={(id) => cancelMutation.mutate(id)}
              onDelete={(id) => deleteMutation.mutate(id)}
              isPending={
                sendMutation.isPending ||
                cancelMutation.isPending ||
                deleteMutation.isPending
              }
              canCancel={!!canCancel}
              canSend={!!canCreate}
              canDelete={!!canCancel}
            />
          </TabsContent>
        </Tabs>
      )}

      <CreateNotificationDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={(data) => createMutation.mutate(data)}
        isPending={createMutation.isPending}
        canSendToAll={!!canSendToAll}
        canSendToSegment={!!canSendToSegment}
        canSendToIndividual={!!canSendToIndividual}
        canSchedule={!!canSchedule}
      />
    </div>
  );
}

function NotificationList({
  notifications,
  onSend,
  onCancel,
  onDelete,
  isPending,
  canCancel,
  canSend,
  canDelete,
}: {
  notifications: Notification[];
  onSend: (id: string) => void;
  onCancel: (id: string) => void;
  onDelete: (id: string) => void;
  isPending: boolean;
  canCancel: boolean;
  canSend: boolean;
  canDelete: boolean;
}) {
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [auditOpenId, setAuditOpenId] = useState<string | null>(null);
  const { pageItems: pageNotifications, ...paginationProps } =
    usePagination(notifications);

  if (notifications.length === 0) {
    return (
      <div className="text-center py-12">
        <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-xl font-medium">No notifications</h3>
        <p className="text-muted-foreground mt-1">
          Notifications will appear here once created
        </p>
      </div>
    );
  }

  const handleCancelConfirm = () => {
    if (cancelConfirmId) {
      onCancel(cancelConfirmId);
      setCancelConfirmId(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      onDelete(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {pageNotifications.map((notification) => {
          const ScopeIcon = scopeIcons[notification.scope];
          return (
            <Card
              key={notification.id}
              data-testid={`notification-${notification.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CardTitle className="text-base">
                        {notification.title}
                      </CardTitle>
                      <Badge className={statusColors[notification.status]}>
                        {notification.status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {categoryLabels[notification.category]}
                      </Badge>
                    </div>
                    <CardDescription className="mt-1 line-clamp-2">
                      {notification.body}
                    </CardDescription>
                  </div>
                  {(() => {
                    const isDraft = notification.status === "draft";
                    const isScheduled = notification.status === "scheduled";
                    const isTerminal =
                      notification.status === "sent" ||
                      notification.status === "failed" ||
                      notification.status === "cancelled";

                    const showSend = isDraft && canSend;
                    const showCancel = (isDraft || isScheduled) && canCancel;
                    // "View Audit" deferred — keep block + AuditDialog wired for re-enable.
                    const showAudit = false && isTerminal;
                    const showDelete = isTerminal && canDelete;

                    // Bug D fix: hide the three-dot trigger entirely when no
                    // actions would render (was showing an empty popover).
                    if (!showSend && !showCancel && !showAudit && !showDelete) {
                      return null;
                    }

                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`button-menu-${notification.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {showSend && (
                            <DropdownMenuItem
                              onClick={() => onSend(notification.id)}
                              disabled={isPending}
                              data-testid={`menu-send-${notification.id}`}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Send Now
                            </DropdownMenuItem>
                          )}
                          {showAudit && (
                            <DropdownMenuItem
                              onClick={() => setAuditOpenId(notification.id)}
                              data-testid={`menu-audit-${notification.id}`}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              View Audit
                            </DropdownMenuItem>
                          )}
                          {showCancel && (
                            <DropdownMenuItem
                              onClick={() =>
                                setCancelConfirmId(notification.id)
                              }
                              disabled={isPending}
                              className="text-destructive"
                              data-testid={`menu-cancel-${notification.id}`}
                            >
                              <X className="h-4 w-4 mr-2" />
                              Cancel
                            </DropdownMenuItem>
                          )}
                          {showDelete && (
                            <DropdownMenuItem
                              onClick={() =>
                                setDeleteConfirmId(notification.id)
                              }
                              disabled={isPending}
                              className="text-destructive"
                              data-testid={`menu-delete-${notification.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete from list
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  })()}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                  <div className="flex items-center gap-1">
                    <ScopeIcon className="h-4 w-4" />
                    <span className="capitalize">{notification.scope}</span>
                    {notification.estimatedRecipients && (
                      <span>
                        ({notification.estimatedRecipients.toLocaleString()}{" "}
                        recipients)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {notification.sentAt
                      ? `Sent ${format(new Date(notification.sentAt), "MMM d, yyyy HH:mm")}`
                      : notification.scheduledAt
                        ? `Scheduled for ${format(new Date(notification.scheduledAt), "MMM d, yyyy HH:mm")}`
                        : `Created ${format(new Date(notification.createdAt), "MMM d, yyyy")}`}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        <PaginationControls
          currentPage={paginationProps.currentPage}
          totalPages={paginationProps.totalPages}
          totalItems={paginationProps.totalItems}
          startIndex={paginationProps.startIndex}
          endIndex={paginationProps.endIndex}
          canGoNext={paginationProps.canGoNext}
          canGoPrev={paginationProps.canGoPrev}
          onPageChange={paginationProps.setPage}
          onNext={paginationProps.nextPage}
          onPrev={paginationProps.prevPage}
        />
      </div>

      <AlertDialog
        open={!!cancelConfirmId}
        onOpenChange={(open) => !open && setCancelConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Notification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this notification? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-dismiss">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-cancel-confirm"
            >
              Cancel Notification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete from list?</AlertDialogTitle>
            <AlertDialogDescription>
              The notification will be hidden from the admin list. Audit entries
              and delivery history remain intact. Recurrence children are also
              hidden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-dismiss">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NotificationAuditDialog
        notificationId={auditOpenId}
        onClose={() => setAuditOpenId(null)}
      />
    </>
  );
}

function NotificationAuditDialog({
  notificationId,
  onClose,
}: {
  notificationId: string | null;
  onClose: () => void;
}) {
  const open = !!notificationId;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["/api/notifications", notificationId, "audit"],
    queryFn: () => notificationsService.audit(notificationId!),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit Log</DialogTitle>
          <DialogDescription>
            Lifecycle entries for this notification (most recent first)
          </DialogDescription>
        </DialogHeader>
        {isLoading && (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Loading audit entries…
          </p>
        )}
        {isError && (
          <p className="text-sm text-destructive py-6 text-center">
            Failed to load audit entries
          </p>
        )}
        {!isLoading && !isError && data && data.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No audit entries recorded
          </p>
        )}
        {!isLoading && !isError && data && data.length > 0 && (
          <div className="space-y-3">
            {data.map((entry) => (
              <div
                key={entry.id}
                className="border rounded-md p-3 space-y-1"
                data-testid={`audit-entry-${entry.id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Badge variant="outline" className="capitalize">
                    {entry.action}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(entry.createdAt), "MMM d, yyyy HH:mm:ss")}
                  </span>
                </div>
                <div className="text-sm grid grid-cols-2 gap-2 text-muted-foreground">
                  <div>scope: {entry.scope}</div>
                  <div>channels: {entry.channels.join(", ")}</div>
                  {entry.recipientCount != null && (
                    <div>recipients: {entry.recipientCount}</div>
                  )}
                  {entry.successCount != null && (
                    <div>success: {entry.successCount}</div>
                  )}
                  {entry.failureCount != null && (
                    <div>failure: {entry.failureCount}</div>
                  )}
                </div>
                {entry.errorSummary && (
                  <p className="text-xs text-muted-foreground italic">
                    {entry.errorSummary}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface AppUserBasic {
  id: string;
  displayName: string;
  maskedEmail: string;
}

interface SegmentFilterSectionProps {
  title: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  testIdPrefix: string;
}

function SegmentFilterSection({
  title,
  options,
  selected,
  onToggle,
  testIdPrefix,
}: SegmentFilterSectionProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-muted-foreground">{title}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Checkbox
              checked={selected.includes(option.value)}
              onCheckedChange={() => onToggle(option.value)}
              data-testid={`checkbox-${testIdPrefix}-${option.value}`}
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function CreateNotificationDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  canSendToAll,
  canSendToSegment,
  canSendToIndividual,
  canSchedule,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateNotificationPayload) => void;
  isPending: boolean;
  canSendToAll: boolean;
  canSendToSegment: boolean;
  canSendToIndividual: boolean;
  canSchedule: boolean;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<NotificationCategory>("system");
  const [scope, setScope] = useState<NotificationScope>("all");
  const [channels, setChannels] = useState<string[]>(["push"]);

  // Segment filters
  const [subscriptionFilter, setSubscriptionFilter] = useState<string[]>([]);
  const [activityFilter, setActivityFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [languageFilter, setLanguageFilter] = useState<string[]>([]);
  const [ageFilter, setAgeFilter] = useState<string[]>([]);
  const [concentrationFilter, setConcentrationFilter] = useState<string[]>([]);
  const [religionFilter, setReligionFilter] = useState<string[]>([]);
  const [relationshipFilter, setRelationshipFilter] = useState<string[]>([]);
  const [childrenFilter, setChildrenFilter] = useState<string[]>([]);

  // Individual targeting
  const [targetUserId, setTargetUserId] = useState<string>("");
  const [userSearch, setUserSearch] = useState("");

  // Repeat settings — full recurrence rule (per-user timezone, end condition).
  const [recurrence, setRecurrence] = useState<RecurrenceRuleValue>(
    DEFAULT_RECURRENCE_RULE,
  );

  // Scheduling
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  // Validation errors
  const [validationError, setValidationError] = useState<string | null>(null);

  // Bug A: reset all form state when dialog closes so reopening starts clean.
  useEffect(() => {
    if (open) return;
    setTitle("");
    setBody("");
    setCategory("system");
    setScope("all");
    setChannels(["push"]);
    setSubscriptionFilter([]);
    setActivityFilter([]);
    setCountryFilter([]);
    setLanguageFilter([]);
    setAgeFilter([]);
    setConcentrationFilter([]);
    setReligionFilter([]);
    setRelationshipFilter([]);
    setChildrenFilter([]);
    setTargetUserId("");
    setUserSearch("");
    setRecurrence(DEFAULT_RECURRENCE_RULE);
    setScheduleEnabled(false);
    setScheduledDate("");
    setScheduledTime("");
    setValidationError(null);
  }, [open]);

  // Fetch users for individual targeting
  const { data: appUsers } = useQuery<AppUserBasic[]>({
    queryKey: ["/api/app-users/overview"],
    enabled: scope === "individual",
  });

  // Recipient preview
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const debouncedScope = useDebounce(scope, 500);

  useEffect(() => {
    if (!open) return;
    const targetFilter =
      scope === "segment"
        ? {
            subscriptionStatus:
              subscriptionFilter.length > 0
                ? (subscriptionFilter as any)
                : undefined,
            appUsageState:
              activityFilter.length > 0 ? (activityFilter as any) : undefined,
            country: countryFilter.length > 0 ? countryFilter : undefined,
            language:
              languageFilter.length > 0 ? (languageFilter as any) : undefined,
            age: ageFilter.length > 0 ? (ageFilter as any) : undefined,
            concentration:
              concentrationFilter.length > 0 ? concentrationFilter : undefined,
            religion: religionFilter.length > 0 ? religionFilter : undefined,
            relationshipStatus:
              relationshipFilter.length > 0 ? relationshipFilter : undefined,
            children:
              childrenFilter.length > 0 ? (childrenFilter as any) : undefined,
          }
        : undefined;

    notificationsService
      .preview({
        scope: debouncedScope as any,
        channels: channels.length > 0 ? (channels as any) : ["push"],
        targetFilter: targetFilter as any,
        targetUserId: scope === "individual" ? targetUserId : undefined,
      })
      .then((r) => setPreviewCount(r.estimatedRecipients))
      .catch(() => setPreviewCount(null));
  }, [
    open,
    debouncedScope,
    channels,
    targetUserId,
    scope,
    subscriptionFilter,
    activityFilter,
    countryFilter,
    languageFilter,
    ageFilter,
    concentrationFilter,
    religionFilter,
    relationshipFilter,
    childrenFilter,
  ]);

  const filteredUsers = appUsers
    ?.filter(
      (user) =>
        user.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.maskedEmail.toLowerCase().includes(userSearch.toLowerCase()),
    )
    .slice(0, 10);

  const selectedUser = appUsers?.find((u) => u.id === targetUserId);

  const toggleSubscriptionFilter = (value: string) => {
    setSubscriptionFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleActivityFilter = (value: string) => {
    setActivityFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleCountryFilter = (value: string) => {
    setCountryFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleLanguageFilter = (value: string) => {
    setLanguageFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleAgeFilter = (value: string) => {
    setAgeFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleConcentrationFilter = (value: string) => {
    setConcentrationFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleReligionFilter = (value: string) => {
    setReligionFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleRelationshipFilter = (value: string) => {
    setRelationshipFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const toggleChildrenFilter = (value: string) => {
    setChildrenFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const hasAnySegmentFilter = () => {
    return (
      subscriptionFilter.length > 0 ||
      activityFilter.length > 0 ||
      countryFilter.length > 0 ||
      languageFilter.length > 0 ||
      ageFilter.length > 0 ||
      concentrationFilter.length > 0 ||
      religionFilter.length > 0 ||
      relationshipFilter.length > 0 ||
      childrenFilter.length > 0
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validation
    if (scope === "individual" && !targetUserId) {
      setValidationError("Please select a user for individual notification");
      return;
    }

    if (scope === "segment" && !hasAnySegmentFilter()) {
      setValidationError(
        "Please select at least one filter for segment targeting",
      );
      return;
    }

    if (scheduleEnabled && (!scheduledDate || !scheduledTime)) {
      setValidationError("Please select both date and time for scheduling");
      return;
    }

    if (recurrence.frequency !== "none" && !scheduleEnabled) {
      setValidationError("Repeating notifications must be scheduled");
      return;
    }

    const recurrenceError = validateRecurrenceRule(recurrence);
    if (recurrenceError) {
      setValidationError(recurrenceError);
      return;
    }

    const targetFilter: NotificationTargetFilter | undefined =
      scope === "segment"
        ? {
            subscriptionStatus:
              subscriptionFilter.length > 0
                ? (subscriptionFilter as any)
                : undefined,
            appUsageState:
              activityFilter.length > 0 ? (activityFilter as any) : undefined,
            country: countryFilter.length > 0 ? countryFilter : undefined,
            language: languageFilter.length > 0 ? languageFilter : undefined,
            age: ageFilter.length > 0 ? ageFilter : undefined,
            concentration:
              concentrationFilter.length > 0 ? concentrationFilter : undefined,
            religion: religionFilter.length > 0 ? religionFilter : undefined,
            relationshipStatus:
              relationshipFilter.length > 0 ? relationshipFilter : undefined,
            children: childrenFilter.length > 0 ? childrenFilter : undefined,
          }
        : undefined;

    // Build scheduled datetime
    let scheduledAt: string | undefined;
    if (scheduleEnabled && scheduledDate && scheduledTime) {
      scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    }

    const repeatFields =
      recurrence.frequency === "none"
        ? {}
        : {
            repeatFrequency: recurrence.frequency,
            repeatTimeOfDay: recurrence.timeOfDay,
            repeatDaysOfWeek:
              recurrence.frequency === "weekly"
                ? recurrence.daysOfWeek
                : undefined,
            repeatDaysOfMonth:
              recurrence.frequency === "monthly"
                ? recurrence.daysOfMonth
                : undefined,
            repeatCount:
              recurrence.endCondition === "count"
                ? (recurrence.count ?? undefined)
                : undefined,
            repeatUntil:
              recurrence.endCondition === "until" && recurrence.until
                ? new Date(`${recurrence.until}T23:59:59Z`).toISOString()
                : undefined,
          };

    onSubmit({
      title,
      body,
      category,
      scope,
      channels: channels as any,
      status: scheduleEnabled ? "scheduled" : "draft",
      targetFilter,
      targetUserId: scope === "individual" ? targetUserId : undefined,
      scheduledAt,
      ...repeatFields,
    } as any);
  };

  const toggleChannel = (channel: string) => {
    setChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Notification</DialogTitle>
          <DialogDescription>
            Compose a new notification for your users. It will be saved as a
            draft.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
              required
              data-testid="input-notification-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Message</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message..."
              className="min-h-[100px]"
              required
              data-testid="input-notification-body"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as NotificationCategory)}
              >
                <SelectTrigger data-testid="select-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="account_billing">
                    Account & Billing
                  </SelectItem>
                  <SelectItem value="content">Content</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Audience</Label>
              <Select
                value={scope}
                onValueChange={(v) => setScope(v as NotificationScope)}
              >
                <SelectTrigger data-testid="select-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {canSendToAll && (
                    <SelectItem value="all">All Users</SelectItem>
                  )}
                  {canSendToSegment && (
                    <SelectItem value="segment">Segment</SelectItem>
                  )}
                  {canSendToIndividual && (
                    <SelectItem value="individual">Individual</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Segment Filters - shown when Segment is selected */}
          {scope === "segment" && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg border max-h-[300px] overflow-y-auto">
              <p className="text-sm font-medium text-foreground sticky top-0 bg-muted/50 pb-2">
                Target users by:
              </p>

              <SegmentFilterSection
                title="Subscription Status"
                options={[
                  { value: "free", label: "Free" },
                  { value: "trial", label: "Trial" },
                  { value: "paid", label: "Paid" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
                selected={subscriptionFilter}
                onToggle={toggleSubscriptionFilter}
                testIdPrefix="sub"
              />

              <SegmentFilterSection
                title="Activity Level"
                options={[
                  { value: "active", label: "Active" },
                  { value: "moderate", label: "Moderate" },
                  { value: "low", label: "Low Activity" },
                  { value: "dormant", label: "Dormant" },
                ]}
                selected={activityFilter}
                onToggle={toggleActivityFilter}
                testIdPrefix="activity"
              />

              <SegmentFilterSection
                title="Country"
                options={[
                  { value: "US", label: "United States" },
                  { value: "BR", label: "Brazil" },
                  { value: "MX", label: "Mexico" },
                  { value: "ES", label: "Spain" },
                  { value: "PT", label: "Portugal" },
                  { value: "AR", label: "Argentina" },
                  { value: "CO", label: "Colombia" },
                  { value: "CL", label: "Chile" },
                  { value: "PE", label: "Peru" },
                  { value: "GB", label: "United Kingdom" },
                  { value: "CA", label: "Canada" },
                  { value: "FR", label: "France" },
                  { value: "DE", label: "Germany" },
                  { value: "IT", label: "Italy" },
                  { value: "AU", label: "Australia" },
                  { value: "IN", label: "India" },
                  { value: "OTHER", label: "Other" },
                ]}
                selected={countryFilter}
                onToggle={toggleCountryFilter}
                testIdPrefix="country"
              />

              <SegmentFilterSection
                title="Language"
                options={[
                  { value: "en", label: "English" },
                  { value: "es", label: "Spanish" },
                  { value: "pt", label: "Portuguese" },
                ]}
                selected={languageFilter}
                onToggle={toggleLanguageFilter}
                testIdPrefix="language"
              />

              <SegmentFilterSection
                title="Age"
                options={[
                  { value: "16-21", label: "16-21" },
                  { value: "21-28", label: "21-28" },
                  { value: "28-40", label: "28-40" },
                  { value: "40-50", label: "40-50" },
                  { value: "50+", label: "50+" },
                ]}
                selected={ageFilter}
                onToggle={toggleAgeFilter}
                testIdPrefix="age"
              />

              <SegmentFilterSection
                title="Concentration"
                options={[
                  { value: "self-love", label: "Self-love and acceptance" },
                  { value: "confidence", label: "Radiant Confidence" },
                  {
                    value: "joy-gratitude",
                    label: "Expanding Joy and Gratitude",
                  },
                  {
                    value: "values-alignment",
                    label: "Living in Alignment with Your Values",
                  },
                  {
                    value: "inner-peace",
                    label: "Cultivating Inner Peace and Balance",
                  },
                  {
                    value: "healing-heartbreak",
                    label: "Healing after heartbreak",
                  },
                  { value: "calling-love", label: "Calling in love" },
                  {
                    value: "mastering-relationships",
                    label: "Mastering relationships",
                  },
                  {
                    value: "professional-growth",
                    label: "Professional growth and confidence",
                  },
                  {
                    value: "clarity-goals",
                    label: "Clarity and direction of your goals",
                  },
                  {
                    value: "leadership",
                    label: "Developing leadership and influence",
                  },
                  { value: "abundance", label: "Abundance and financial flow" },
                  {
                    value: "daily-rituals",
                    label: "Cultivating daily rituals for success",
                  },
                  {
                    value: "hormonal-cycle",
                    label: "Living in sync with your hormonal cycle",
                  },
                  {
                    value: "lunar-energy",
                    label: "Aligning with lunar energy and moon phases",
                  },
                  {
                    value: "astrology",
                    label: "Using astrology for self-discovery",
                  },
                  {
                    value: "emotional-resilience",
                    label: "Emotional resilience and strength",
                  },
                  {
                    value: "releasing-stress",
                    label: "Releasing stress and restoring calm",
                  },
                ]}
                selected={concentrationFilter}
                onToggle={toggleConcentrationFilter}
                testIdPrefix="concentration"
              />

              <SegmentFilterSection
                title="Religion/Spirituality"
                options={[
                  { value: "christianity", label: "Christianity" },
                  { value: "hindu", label: "Hindu" },
                  { value: "buddhist", label: "Buddhist" },
                  { value: "islam", label: "Islam" },
                  { value: "judaism", label: "Judaism" },
                  { value: "catholic", label: "Catholic" },
                  {
                    value: "general-spirituality",
                    label: "General Spirituality",
                  },
                ]}
                selected={religionFilter}
                onToggle={toggleReligionFilter}
                testIdPrefix="religion"
              />

              <SegmentFilterSection
                title="Relationship Status"
                options={[
                  { value: "single-fun", label: "Single, looking for fun" },
                  { value: "married", label: "Married" },
                  { value: "in-relationship", label: "In a relationship" },
                  {
                    value: "single-dating",
                    label: "Single, dating without intention",
                  },
                  {
                    value: "single-looking",
                    label: "Single, looking for a relationship",
                  },
                  {
                    value: "single-not-looking",
                    label: "Single, not looking for a relationship",
                  },
                  { value: "healing", label: "Healing from past relationship" },
                ]}
                selected={relationshipFilter}
                onToggle={toggleRelationshipFilter}
                testIdPrefix="relationship"
              />

              <SegmentFilterSection
                title="Children"
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                selected={childrenFilter}
                onToggle={toggleChildrenFilter}
                testIdPrefix="children"
              />

              {!hasAnySegmentFilter() && (
                <p className="text-xs text-muted-foreground italic">
                  Select at least one filter to target specific users
                </p>
              )}
            </div>
          )}

          {/* Individual User Picker - shown when Individual is selected */}
          {scope === "individual" && (
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
              <p className="text-sm font-medium text-foreground">
                Select a user:
              </p>

              {selectedUser ? (
                <div className="flex items-center justify-between p-3 bg-background rounded-md border">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {selectedUser.displayName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {selectedUser.maskedEmail}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setTargetUserId("")}
                    data-testid="button-clear-user"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="pl-9"
                      data-testid="input-user-search"
                    />
                  </div>

                  {filteredUsers && filteredUsers.length > 0 ? (
                    <div className="max-h-[200px] overflow-y-auto space-y-1">
                      {filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setTargetUserId(user.id);
                            setUserSearch("");
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover-elevate text-left"
                          data-testid={`button-select-user-${user.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {user.displayName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {user.maskedEmail}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : userSearch ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No users found matching "{userSearch}"
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      Start typing to search for a user
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Channels</Label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={channels.includes("push")}
                    onCheckedChange={() => toggleChannel("push")}
                    data-testid="checkbox-push"
                  />
                  <span className="text-sm">Push</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={channels.includes("email")}
                    onCheckedChange={() => toggleChannel("email")}
                    data-testid="checkbox-email"
                  />
                  <span className="text-sm">Email</span>
                </label>
                {/* In-app channel deferred — see notifications design spec out-of-scope */}
                {/* <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={channels.includes("in_app")}
                    onCheckedChange={() => toggleChannel("in_app")}
                    data-testid="checkbox-in-app"
                  />
                  <span className="text-sm">In-App</span>
                </label> */}
              </div>
            </div>

            <RecurrenceRulePicker value={recurrence} onChange={setRecurrence} />
          </div>

          {/* Scheduling */}
          {canSchedule && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={scheduleEnabled}
                  onCheckedChange={(checked) =>
                    setScheduleEnabled(checked === true)
                  }
                  data-testid="checkbox-schedule"
                />
                <span className="text-sm font-medium">Schedule for later</span>
              </label>

              {scheduleEnabled && (
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Date
                    </Label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      data-testid="input-schedule-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Time
                    </Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      data-testid="input-schedule-time"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Validation Error */}
          {validationError && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-sm text-destructive">{validationError}</p>
            </div>
          )}

          {previewCount !== null && (
            <div className="text-sm text-muted-foreground italic">
              ~{previewCount.toLocaleString()} user
              {previewCount === 1 ? "" : "s"} will receive this
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              data-testid="button-submit-notification"
            >
              {isPending
                ? "Creating..."
                : scheduleEnabled
                  ? "Schedule"
                  : "Create Draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
